// @ts-check
import React from 'react';
import {
  OutlinedChartBarIcon,
  SearchIcon,
  BarsIcon,
  ExclamationCircleIcon,
  AngleLeftIcon,
  CircleIcon,
  ExpandIcon,
  CompressIcon,
  CloseIcon,
  EditIcon,
  MapIcon,
  MapMarkerIcon,
  HelpIcon,
  ConnectedIcon,
  DisconnectedIcon,
  UnknownIcon,
  CheckIcon,
  UploadIcon
} from '@patternfly/react-icons';
// TODO: Replace with patternfly
import {
  AnalyticsReference,
  Apps,
  Asset,
  CharacterWholeNumber,
  ChartLine,
  Chat,
  CollapseAll,
  Dashboard,
  DataFormat,
  DataVis_1,
  Devices,
  Enterprise,
  ExpandAll,
  Group,
  Home,
  Information,
  Integration,
  List,
  Login,
  Logout,
  Meter,
  Microservices_2,
  ModelBuilder,
  Notification,
  ParentChild,
  Table,
  Tools,
  TrashCan,
  TreeViewAlt,
  Unlink,
  User,
  UserRole,
  WarningAlt,
  WatsonHealthTextAnnotationToggle
} from '@carbon/icons-react';
import _ from 'lodash';
import PropTypes from 'prop-types';

/**
 * @readonly
 * @enum {string}
 */
export const Size = {
  SMALL: 'sm',
  MEDIUM: 'md',
  LARGE: 'lg',
  XL: 'xl'
};

/**
 * @param {any} icon
 * @returns {React.FunctionComponent<Icon>}
 */
const icon = (icon) => {
  const Component = ({ size, ...props }) => {
    switch (size) {
      case Size.SMALL:
        return React.createElement(icon, { ...props, size: 16 });
      case Size.MEDIUM:
        return React.createElement(icon, { ...props, size: 20 });
      case Size.LARGE:
        return React.createElement(icon, { ...props, size: 24 });
      case Size.XL:
        return React.createElement(icon, { ...props, size: 32 });
      default:
        return React.createElement(icon, { ...props, size: 16 });
    }
  };
  Component.propTypes = {
    size: PropTypes.oneOf(_.values(Size))
  };
  return Component;
};

/**
 * @typedef {Object} IconWrapperProps
 * @prop {any} component
 * @prop {Size} [size]
 */

/**
 *
 * @param {IconWrapperProps} props
 * @returns {JSX.Element}
 */
const IconWrapper = ({ component, size, ...props }) => {
  if (size === Size.SMALL) {
    return React.createElement(component, { ...props, size: size });
  }


  // return React.createElement(medium, props);
  return React.createElement(component, { ...props, size: size });
};

/**
 * @typedef {object} Icon
 * @prop {Size=} size
 */

/**
 * Create a patternfly icon
 * @param {any} icon
 * @returns {React.FunctionComponent<Icon>}
 */
const PatternflyIcon = (icon) => {
  return ({ size, ...props }) => <IconWrapper component={icon} size={size} {...props} />;
};

/**
 * @readonly
 * @type {Record<string, React.FunctionComponent<Icon>>}
 */
const Icons = {
  AnalyticsReference: icon(AnalyticsReference),
  Apps: icon(Apps),
  Asset: icon(Asset),
  BindingModel: icon(ModelBuilder),
  ChartLine: icon(ChartLine),
  Checkmark: PatternflyIcon(CheckIcon),
  Component: icon(Microservices_2),
  CollapseAll: icon(CollapseAll),
  Dashboard: icon(Dashboard),
  DataVis: icon(DataVis_1),
  Delete: icon(TrashCan),
  Devices: icon(Devices),
  Dial: icon(Meter),
  DtdlModel: icon(DataFormat),
  Enterprise: icon(Enterprise),
  Error: PatternflyIcon(ExclamationCircleIcon),
  ExpandAll: icon(ExpandAll),
  Group: icon(Group),
  Home: icon(Home),
  Help: PatternflyIcon(HelpIcon),
  Info: icon(Information),
  Login: icon(Login),
  Logout: icon(Logout),
  Map: PatternflyIcon(MapIcon),
  MapMarker: PatternflyIcon(MapMarkerIcon),
  Messages: icon(Chat),
  Notification: icon(Notification),
  ParentChild: icon(ParentChild),
  Provider: icon(Integration),
  Properties: PatternflyIcon(List),
  Search: PatternflyIcon(SearchIcon),
  Signals: PatternflyIcon(OutlinedChartBarIcon),
  Table: icon(Table),
  Telemetry: icon(CharacterWholeNumber),
  TextAnnotationToggle: icon(WatsonHealthTextAnnotationToggle),
  Tools: icon(Tools),
  TreeViewAlt: icon(TreeViewAlt),
  Unlink: icon(Unlink),
  User: icon(User),
  UserRole: icon(UserRole),
  Upload: icon(UploadIcon),
  Warning: icon(WarningAlt),
  NavHamburger: PatternflyIcon(BarsIcon),
  AngleLeftIcon: PatternflyIcon(AngleLeftIcon),
  CircleIcon: PatternflyIcon(CircleIcon),
  Fullscreen: PatternflyIcon(ExpandIcon),
  ExitFullscreen: PatternflyIcon(CompressIcon),
  Close: PatternflyIcon(CloseIcon),
  Edit: PatternflyIcon(EditIcon),
  Connected: PatternflyIcon(ConnectedIcon),
  Disconnected: PatternflyIcon(DisconnectedIcon),
  Unknown: PatternflyIcon(UnknownIcon)
};

export default Icons;
